import moment from 'moment-timezone';
import { DateFormatObject, CancellationObject } from 'common/types';
import countriesAndTimezones, { Timezone } from 'countries-and-timezones';
import { TimeUnit, TimeUnits } from 'common/constants/timeUnits';
import { TFunction } from 'i18next';
import {
	CountryCode,
	getDateFormatByCountryCode,
	getStartDayByCountryCode,
	getTimeFormatByCountryCode,
} from 'common/modules/atoms/countries';

export const TIME_FORMATS = ['HH:mm', 'hh:mm A'] as const;

export type TimeFormat = typeof TIME_FORMATS[number];

export const DATE_FORMATS = ['DD.MM.YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'] as const;

export type DateFormat = typeof DATE_FORMATS[number];

export const START_DAYS = ['monday', 'saturday', 'sunday'] as const;

export type StartDay = typeof START_DAYS[number];

export const getCountryTimeZones = (country: string) => {
	return countriesAndTimezones.getTimezonesForCountry(country);
};

export const getCountryFromTimezone = (timezone: string) => {
	return countriesAndTimezones.getCountryForTimezone(timezone);
};

export const getAllTimeZones = () => {
	const getAllTimeZones = countriesAndTimezones.getAllTimezones();
	const sorted = Object.keys(getAllTimeZones).sort((a, b) => {
		const aNameArray = a.split('/');
		const bNameArray = b.split('/');
		return aNameArray[aNameArray.length - 1].localeCompare(bNameArray[bNameArray.length - 1]);
	});
	const sortedTimeZones = sorted.map((name) => getAllTimeZones[name]);
	return sortedTimeZones;
};

export const getFormattedTimeZone = (timeZone: Timezone) => {
	const nameArray = timeZone.name.split('/');
	const nameOfTimeZone = nameArray[nameArray.length - 1].replace('_', ' ');
	return nameOfTimeZone + ' - (' + moment.tz(timeZone.name).format('z Z') + ')';
};

export const getDefaultDateObject = (): DateFormatObject => ({
	dateFormat: 'DD.MM.YYYY',
	timeFormat: 'HH:mm',
	startDay: 'monday',
});

export const getDefaultTimezone = (): string => moment.tz.guess();

const dateFormats = [
	'HH',
	'HH:mm',
	'ddd HH:mm',
	'dddd HH:mm',
	'D.M., HH:mm',
	'DD.MM., HH:mm',
	'DD.MM.',
	'ddd D.M',
	'ddd DD.MM.',
	'ddd D.M., HH:mm',
	'ddd DD.MM. HH:mm',
	'dddd DD.MM. HH:mm',
	'dddd DD.MM.',
	'DD.MM.YYYY',
	'ddd DD.MM.YYYY',
	'dddd DD.MM.YYYY',
	'ddd D.M.YYYY',
	'ddd D.M.YYYY HH:mm',
	'ddd. MMM D',
	'ddd. MMM D, HH:mm',
	'DD.MM.YYYY, HH:mm',
	'MMMM Do YYYY',
	'MMMM Do YYYY, HH:mm',
	'MMMM Do YYYY, HH:mm:ss',
	'MMM DD, YYYY',
	'MMM DD, HH:mm',
] as const;

export type MomentDateFormat = typeof dateFormats[number];

const dateFormatMap = {
	'MM/DD/YYYY': {
		'D.M.': 'M/D',
		'D.M.,': 'M/D,',
		'DD.MM.': 'MM/DD',
		'DD.MM.,': 'MM/DD,',
		'D.M.YYYY': 'M/D/YYYY',
		'DD.MM.YYYY': 'MM/DD/YYYY',
		'DD.MM.YYYY,': 'MM/DD/YYYY,',
	},
	'YYYY-MM-DD': {
		'D.M.': 'M-D',
		'D.M.,': 'M-D,',
		'DD.MM.': 'MM-DD',
		'DD.MM.,': 'MM-DD,',
		'D.M.YYYY': 'YYYY-M-D',
		'DD.MM.YYYY': 'YYYY-MM-DD',
		'DD.MM.YYYY,': 'YYYY-MM-DD,',
	},
	'hh:mm A': {
		HH: 'hh A',
		'HH:mm': 'hh:mm A',
		'HH:mm:ss': 'hh:mm:ss A',
	},
};

export const localFormat = (
	dateTime: string | moment.Moment | undefined,
	dateFormat: MomentDateFormat,
	shopDateFormat: DateFormatObject,
) => {
	return moment(dateTime).format(localFormatString(dateFormat, shopDateFormat));
};

export const localFormatString = (
	dateFormat: MomentDateFormat,
	shopDateFormat: DateFormatObject,
): string => {
	let returnString = '';
	const splitDateFormat = dateFormat.split(' ');
	splitDateFormat.forEach((string, index) => {
		const localString =
			dateFormatMap[shopDateFormat.dateFormat]?.[string] ??
			dateFormatMap[shopDateFormat.timeFormat]?.[string] ??
			string;
		returnString += localString + (index !== splitDateFormat.length - 1 ? ' ' : '');
	});
	return returnString;
};

export type FirstDayOfWeek = 0 | 1 | 3 | 4 | 5 | undefined;

export const dayTypesToNumbers = {
	monday: [1],
	tuesday: [2],
	wednesday: [3],
	thursday: [4],
	friday: [5],
	saturday: [6],
	sunday: [0],
	weekDays: [1, 2, 3, 4, 5],
	weekends: [0, 6],
	allDays: [0, 1, 2, 3, 4, 5, 6],
};

export const firstDaysOfWeek: { [key: string]: 0 | 1 | 6 } = {
	saturday: 6,
	sunday: 0,
	monday: 1,
};

export const momentToDateInShopTimezone = (date: moment.Moment) =>
	new Date(moment(date).format('YYYY/MM/DD HH:mm:ss'));

export const getHoursAndMinutesFromHourMinuteString = (hoursAndMinutes: string) => {
	const [hours, minutes] = hoursAndMinutes.split(':').map((val) => Number(val));
	return [hours, minutes];
};

export const convertSeconds = (seconds: number, to: 'hours' | 'days') => {
	const timeInSeconds: Record<typeof to, number> = {
		hours: 1 / (60 * 60),
		days: 1 / (60 * 60 * 24),
	};
	return seconds * timeInSeconds[to];
};

export const formatTime = (time: number, opts: { precision: number; suffix: 'h' | 'd' }) => {
	const timePrecision = parseFloat(time.toFixed(opts.precision));
	return `${timePrecision} ${opts.suffix}`;
};

export const convertHoursToTimeUnit = (hours: number, unit: TimeUnit) => {
	const convert = {
		[TimeUnits.hours]: hours,
		[TimeUnits.days]: hours / 24,
		[TimeUnits.weeks]: hours / 24 / 7,
	};
	return convert[unit] ?? hours;
};

export const convertTimeUnitsToHours = (value: number, unit: TimeUnit) => {
	const convert = {
		[TimeUnits.hours]: value,
		[TimeUnits.days]: value * 24,
		[TimeUnits.weeks]: value * 24 * 7,
	};
	return convert[unit] ?? value;
};

export const convertToTimeUnitHours = (unitHours: number, unitFrom: TimeUnit, unitTo: TimeUnit) => {
	const { hours, days, weeks } = TimeUnits;
	const convert = {
		[`${hours}_${days}`]: unitHours * 24,
		[`${hours}_${weeks}`]: unitHours * 7 * 24,
		[`${days}_${hours}`]: unitHours / 24,
		[`${days}_${weeks}`]: unitHours * 7,
		[`${weeks}_${hours}`]: unitHours / 7 / 24,
		[`${weeks}_${days}`]: unitHours / 7,
	};
	return convert[`${unitFrom}_${unitTo}`] ?? unitHours;
};

export const cancellationHoursAsString = (cancellation: CancellationObject, t: TFunction) => {
	const { beforeHours: hours, displayAs: unit } = cancellation;
	const value = convertHoursToTimeUnit(hours || 0, unit ?? 'hours');

	if (unit === TimeUnits.days) {
		return hours === 24
			? `${value} ${t('common:times.day')}`
			: `${value} ${t('common:times.days')}`;
	} else if (unit === TimeUnits.weeks) {
		return hours === 168
			? `${value} ${t('common:times.week')}`
			: `${value} ${t('common:times.weeks')}`;
	} else {
		return hours === 1
			? `${value} ${t('common:times.hour')}`
			: `${value} ${t('common:times.hours')}`;
	}
};

export const yearMonthDayAsMoment = ({
	year,
	month,
	day,
}: {
	month: number;
	year: number;
	day: number;
}) =>
	moment()
		.year(year)
		.month(month - 1) //Moment indexes months from 0-11
		.date(day)
		.startOf('day');

export const getYearMonthDayFromMoment = (date: moment.Moment) => ({
	year: date.year(),
	month: date.month() + 1, //Moment indexes months from 0-11
	day: date.date(),
});

export const getDateFormatFromCountry = (countryCode: CountryCode): DateFormatObject => {
	return {
		dateFormat: getDateFormatByCountryCode(countryCode),
		timeFormat: getTimeFormatByCountryCode(countryCode),
		startDay: getStartDayByCountryCode(countryCode),
	};
};
